/**
 * Cosumy Line Consolidator (v2)
 * --------------------------------------------------------------------------
 * Shopify's automatic gift discount can split one variant into multiple cart
 * lines. This module keeps the UI aligned with server truth:
 *
 * 1) Display qty always comes from data-cart-quantity (variant total from Liquid
 *    / cart.js) – never from summing per-line value attributes or .value.
 * 2) Duplicate rows for the same variant are folded visually (hide extras, sum prices).
 * 3) Server merge (clear+add) runs only when idle – never during user writes.
 */
(function () {
  'use strict';

  var GIFT_VARIANT_ID = null;
  var CONSOLIDATED_ATTR = 'data-cosumy-consolidated';
  var mergeInflight = false;
  var serverTotals = null;
  var serverTotalsAt = 0;
  var SERVER_CACHE_MS = 400;
  var userIntentByVariant = Object.create(null);
  var userIntentUntil = Object.create(null);
  var userIntentServerQty = Object.create(null);
  var USER_INTENT_MS = 8000;
  var USER_INTENT_PENDING_MS = 30000;

  function setUserIntent(vid, qty) {
    if (!vid || !Number.isFinite(qty)) return;
    var key = String(vid);
    userIntentByVariant[key] = qty;
    userIntentUntil[key] = Date.now() + USER_INTENT_MS;
  }

  function noteServerQty(vid, qty) {
    if (!vid || !Number.isFinite(qty)) return;
    var key = String(vid);
    userIntentServerQty[key] = qty;
    if (userIntentByVariant[key] === qty) {
      clearUserIntent(vid);
    }
  }

  function getActiveUserIntent(vid) {
    var key = String(vid);
    var intent = userIntentByVariant[key];
    if (intent === undefined) return null;

    var mgr = window.CosumyGiftManager;
    var writesPending = mgr && typeof mgr.hasPendingWrites === 'function' && mgr.hasPendingWrites();
    var serverQty = userIntentServerQty[key];
    var serverMatches = serverQty !== undefined && serverQty === intent;

    if (writesPending || !serverMatches) {
      userIntentUntil[key] = Date.now() + USER_INTENT_PENDING_MS;
      return intent;
    }

    if (!userIntentUntil[key] || Date.now() >= userIntentUntil[key]) {
      delete userIntentByVariant[key];
      delete userIntentUntil[key];
      delete userIntentServerQty[key];
      return null;
    }
    return intent;
  }

  function clearUserIntent(vid) {
    var key = String(vid);
    delete userIntentByVariant[key];
    delete userIntentUntil[key];
    delete userIntentServerQty[key];
  }

  function lineQtyFromServer(input) {
    var raw = input.getAttribute('value');
    var n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  }

  /** Variant total for display and +/- – data-cart-quantity first, then value attr. */
  function variantTotalFromInput(input) {
    var cartTotal = input.dataset.cartQuantity;
    if (cartTotal !== undefined && cartTotal !== '') {
      var n = parseInt(cartTotal, 10);
      if (Number.isFinite(n)) return n;
    }
    return lineQtyFromServer(input);
  }

  function variantTotalInDom(vid) {
    var inputs = document.querySelectorAll('.quantity__input[data-quantity-variant-id="' + vid + '"]');
    if (!inputs.length) return 0;
    var visible = [];
    inputs.forEach(function (inp) {
      var row = inp.closest('.cart-item');
      if (row && row.style.display !== 'none' && row.getAttribute('aria-hidden') !== 'true') {
        visible.push(inp);
      }
    });
    var use = visible.length ? visible : Array.prototype.slice.call(inputs);
    for (var i = 0; i < use.length; i++) {
      var cq = parseInt(use[i].dataset.cartQuantity, 10);
      if (Number.isFinite(cq)) return cq;
    }
    var primary = use[0];
    var displayVal = parseInt(primary.value, 10);
    if (Number.isFinite(displayVal)) return displayVal;
    var attrVal = parseInt(primary.getAttribute('value'), 10);
    return Number.isFinite(attrVal) ? attrVal : 0;
  }

  function shouldPause() {
    var mgr = window.CosumyGiftManager;
    if (mgr && typeof mgr.hasPendingWrites === 'function' && mgr.hasPendingWrites()) return true;
    return false;
  }

  function parsePrice(text) {
    if (!text) return 0;
    var clean = String(text).replace(/[^0-9,.\-]/g, '');
    if (clean.indexOf(',') !== -1) clean = clean.replace(/\./g, '').replace(',', '.');
    var n = parseFloat(clean);
    return Number.isFinite(n) ? n : 0;
  }

  function formatPriceLike(template, amount) {
    var hasEuro = /€/.test(template);
    var hasComma = /,\d{2}/.test(template);
    var formatted = amount.toFixed(2);
    if (hasComma) formatted = formatted.replace('.', ',');
    return (hasEuro ? '€' : '') + formatted + (hasEuro ? '' : '');
  }

  function isGiftRow(row) {
    if (!row) return false;
    if (row.getAttribute('data-gift') === 'true') return true;
    return !!row.querySelector('.cart-item__gift-qty');
  }

  function findQtyInput(row) {
    return row.querySelector('.quantity__input[data-quantity-variant-id]');
  }

  function findPriceTargets(row) {
    return Array.prototype.slice
      .call(row.querySelectorAll('.cart-item__totals .price.price--end:not(.cart-item__gift-free)'))
      .filter(function (el) {
        return !el.closest('.cart-item__discounted-prices');
      });
  }

  function mergeDiscountLabels(primary, hiddenRows) {
    var primaryList = primary.querySelector('.discounts');
    if (!primaryList) return;
    var seen = Object.create(null);
    primaryList.querySelectorAll('.discounts__discount').forEach(function (li) {
      var label = (li.textContent || '').replace(/\s+/g, ' ').trim();
      if (label) seen[label] = true;
    });
    hiddenRows.forEach(function (row) {
      var list = row.querySelector('.discounts');
      if (!list) return;
      list.querySelectorAll('.discounts__discount').forEach(function (li) {
        var label = (li.textContent || '').replace(/\s+/g, ' ').trim();
        if (!label || seen[label]) return;
        seen[label] = true;
        primaryList.appendChild(li.cloneNode(true));
      });
    });
  }

  function targetQtyForDuplicateLines(variantItems) {
    var qtys = variantItems.map(function (i) {
      return i.quantity || 0;
    });
    if (qtys.length < 2) return qtys[0] || 0;

    var allEqual = qtys.every(function (q) {
      return q === qtys[0];
    });
    if (!allEqual) return qtys.reduce(function (a, b) { return a + b; }, 0);
    if (qtys[0] === 1) return qtys.length;

    var hasDiscounted = variantItems.some(function (i) {
      return (i.line_level_discount_allocations || []).length > 0;
    });
    var hasUndiscounted = variantItems.some(function (i) {
      return !(i.line_level_discount_allocations || []).length;
    });
    if (hasDiscounted && hasUndiscounted) {
      return qtys.reduce(function (a, b) { return a + b; }, 0);
    }

    var unitPrices = variantItems.map(function (i) {
      return (i.final_line_price || 0) / (i.quantity || 1);
    });
    if (Math.max.apply(null, unitPrices) - Math.min.apply(null, unitPrices) > 1) {
      return qtys.reduce(function (a, b) { return a + b; }, 0);
    }
    return qtys[0];
  }

  async function fetchServerTotals() {
    var now = Date.now();
    if (serverTotals && now - serverTotalsAt < SERVER_CACHE_MS) return serverTotals;
    try {
      var res = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!res.ok) return serverTotals;
      var cart = await res.json();
      var by = Object.create(null);
      (cart.items || []).forEach(function (item) {
        var vid = String(item.variant_id);
        if (!by[vid]) by[vid] = 0;
        by[vid] += item.quantity;
      });
      serverTotals = by;
      serverTotalsAt = now;
      return by;
    } catch (e) {
      return serverTotals;
    }
  }

  async function syncDatasetFromServer() {
    if (shouldPause()) return;
    var by = await fetchServerTotals();
    if (!by) return;
    document.querySelectorAll('.quantity__input[data-quantity-variant-id]').forEach(function (inp) {
      var vid = inp.dataset.quantityVariantId;
      if (!vid || by[vid] === undefined) return;
      var intent = getActiveUserIntent(vid);
      if (intent !== null) {
        if (by[vid] === intent) noteServerQty(vid, intent);
        inp.dataset.cartQuantity = String(intent);
        return;
      }
      noteServerQty(vid, by[vid]);
      inp.dataset.cartQuantity = String(by[vid]);
    });
  }

  function consolidateRoot(root) {
    if (!root) return;
    var rows = Array.prototype.slice.call(root.querySelectorAll('.cart-item'));
    if (!rows.length) return;

    var groups = Object.create(null);
    var order = [];

    rows.forEach(function (row) {
      if (row.style.display === 'none') row.style.display = '';
      row.removeAttribute('aria-hidden');
      if (isGiftRow(row)) return;
      var input = findQtyInput(row);
      if (!input) return;
      var vid = input.dataset.quantityVariantId;
      if (!vid) return;
      if (!groups[vid]) {
        groups[vid] = { rows: [], variantTotal: 0, prices: [] };
        order.push(vid);
      }
      groups[vid].rows.push(row);
      var rowTotal = variantTotalFromInput(input);
      if (rowTotal > groups[vid].variantTotal) groups[vid].variantTotal = rowTotal;
      findPriceTargets(row).forEach(function (el) {
        groups[vid].prices.push(parsePrice(el.textContent));
      });
    });

    if (serverTotals) {
      order.forEach(function (vid) {
        if (serverTotals[vid] !== undefined && getActiveUserIntent(vid) === null) {
          groups[vid].variantTotal = serverTotals[vid];
        }
      });
    }

    order.forEach(function (vid) {
      var g = groups[vid];
      var primary = g.rows[0];
      var primaryInput = findQtyInput(primary);
      if (!primaryInput) return;

      var intentQty = getActiveUserIntent(vid);
      var displayQty = intentQty !== null ? intentQty : g.variantTotal;

      if (g.rows.length < 2) {
        primary.removeAttribute(CONSOLIDATED_ATTR);
        if (shouldPause()) return;
        if (primaryInput === document.activeElement) return;
        if (parseInt(primaryInput.value, 10) !== displayQty) {
          primaryInput.value = String(displayQty);
        }
        primaryInput.dataset.cartQuantity = String(displayQty);
        return;
      }

      var hiddenRows = [];
      for (var i = 1; i < g.rows.length; i++) {
        g.rows[i].style.display = 'none';
        g.rows[i].setAttribute('aria-hidden', 'true');
        hiddenRows.push(g.rows[i]);
      }
      mergeDiscountLabels(primary, hiddenRows);

      if (shouldPause()) return;
      if (primaryInput === document.activeElement) return;

      primaryInput.value = String(displayQty);
      primaryInput.dataset.cartQuantity = String(displayQty);
      primaryInput.dataset.consolidatedDisplayQty = String(displayQty);
      primary.setAttribute(CONSOLIDATED_ATTR, 'true');

      var combinedPrice = g.prices.reduce(function (a, b) { return a + b; }, 0);
      findPriceTargets(primary).forEach(function (el) {
        el.textContent = formatPriceLike(el.textContent, combinedPrice);
      });
    });

    var giftRows = [];
    rows.forEach(function (row) {
      if (isGiftRow(row)) giftRows.push(row);
    });
    if (giftRows.length > 1) {
      for (var gi = 1; gi < giftRows.length; gi++) {
        giftRows[gi].style.display = 'none';
        giftRows[gi].setAttribute('aria-hidden', 'true');
      }
    }
  }

  async function applyEverywhere() {
    if (shouldPause()) return;
    await syncDatasetFromServer();
    document.querySelectorAll('cart-items, cart-drawer-items').forEach(consolidateRoot);
  }

  function scheduleApply() {
    if (scheduleApply._t) clearTimeout(scheduleApply._t);
    scheduleApply._t = setTimeout(function () {
      scheduleApply._t = null;
      applyEverywhere();
    }, 60);
  }

  async function mergeDuplicateVariants() {
    if (mergeInflight || shouldPause()) return false;
    mergeInflight = true;
    try {
      var res = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!res.ok) return false;
      var cart = await res.json();
      var items = cart.items || [];
      if (items.length < 2) return false;

      var lineCounts = Object.create(null);
      items.forEach(function (item) {
        if (GIFT_VARIANT_ID && item.variant_id === GIFT_VARIANT_ID) return;
        var vid = String(item.variant_id);
        lineCounts[vid] = (lineCounts[vid] || 0) + 1;
      });

      var updates = Object.create(null);
      var needs = false;
      Object.keys(lineCounts).forEach(function (vid) {
        if (lineCounts[vid] <= 1) return;
        var variantItems = items.filter(function (item) {
          if (GIFT_VARIANT_ID && item.variant_id === GIFT_VARIANT_ID) return false;
          return String(item.variant_id) === vid;
        });
        var intent = getActiveUserIntent(vid);
        var serverSum = variantItems.reduce(function (a, item) {
          return a + (item.quantity || 0);
        }, 0);
        if (intent !== null) {
          if (serverSum === intent) {
            noteServerQty(vid, intent);
            return;
          }
          updates[vid] = intent;
        } else {
          var target = targetQtyForDuplicateLines(variantItems);
          updates[vid] = target < serverSum ? target : serverSum;
        }
        needs = true;
      });
      if (!needs) return false;

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      };
      var vids = Object.keys(updates);
      for (var i = 0; i < vids.length; i++) {
        var vid = vids[i];
        var qty = updates[vid];
        var zero = {};
        zero[vid] = 0;
        var z = await fetch(root + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: headers,
          body: JSON.stringify({ updates: zero }),
        });
        if (!z.ok) return false;
        if (qty > 0) {
          var a = await fetch(root + 'cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: headers,
            body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: qty }] }),
          });
          if (!a.ok) return false;
        }
      }
      serverTotals = null;
      return true;
    } catch (e) {
      return false;
    } finally {
      mergeInflight = false;
    }
  }

  try {
    var cached = localStorage.getItem('cosumy_gift_variant_id_v2');
    if (cached) GIFT_VARIANT_ID = parseInt(cached, 10);
  } catch (e) { /* noop */ }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleApply);
  } else {
    scheduleApply();
  }

  function trySubscribe() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleApply);
      return true;
    }
    return false;
  }
  if (!trySubscribe()) {
    var tries = 0;
    var t = setInterval(function () {
      if (trySubscribe() || ++tries > 30) clearInterval(t);
    }, 200);
  }
  document.addEventListener('cart:updated', scheduleApply);
  document.addEventListener('cart:change', scheduleApply);

  window.CosumyLineConsolidator = {
    apply: applyEverywhere,
    merge: mergeDuplicateVariants,
    mergeAndApply: async function () {
      var merged = await mergeDuplicateVariants();
      await applyEverywhere();
      return merged;
    },
    variantTotalInDom: variantTotalInDom,
    setUserIntent: setUserIntent,
    clearUserIntent: clearUserIntent,
    getActiveUserIntent: getActiveUserIntent,
    noteServerQty: noteServerQty,
    invalidateServerCache: function () {
      serverTotals = null;
    },
  };
})();
